/**
 * Website Scanner Teaser — /scanner
 *
 * Value-First lead magnet: paste a URL → watch Orkexra "scan" the
 * site → get a preview report → sign up to unlock. Every check is
 * simulated on the client so the experience is instant and can never
 * fail on a live-vendor outage.
 *
 * This is the shell for the future real Website Scanner (Business
 * Brain™). When the backend endpoint ships, only the runScan()
 * function needs to swap from a timed animation to a fetch call.
 */
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertCircle, Check, ChevronRight, Globe2, Loader2, Radar,
  Search, Sparkles, Target, TrendingUp, Zap,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { captureWaitlist } from "@/shared/api/waitlist";
import { config } from "@/shared/config";

/* ── Real-scanner API ────────────────────────────────────────────── */
interface ScanCampaign {
  title: string; objective: string; primary_channel: string;
  expected_outcome: string; why_it_fits: string;
}
interface ScanFinding {
  category: string; score: number;
  strengths: string[]; opportunities: string[]; commentary: string;
}
interface ScanBrandPreview {
  voice: string; audience: string;
  products_services: string[]; offers: string[];
  core_messaging: string[]; brand_personality: string[];
}
export interface ScanResponse {
  url: string; host: string; industry: string; industry_label: string;
  overall_score: number;
  scores: { voice: number; messaging: number; trust: number; foundation: number; opportunity: number; visual_hierarchy: number };
  executive_summary: string;
  findings: ScanFinding[];
  campaigns: ScanCampaign[];
  content_ideas: string[]; quick_wins: string[];
  brand_brain_preview: ScanBrandPreview;
  confidence: number; elapsed_ms: number;
}

async function runExecutiveScan(url: string): Promise<ScanResponse> {
  const endpoint = `${config.backendUrl}${config.apiPrefix}/business-discovery/scan`;
  const resp = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  if (!resp.ok) {
    let msg = "We couldn't complete the assessment. Please try again in a moment.";
    try {
      const j = await resp.json();
      if (typeof j?.detail === "string") msg = j.detail;
    } catch { /* keep default */ }
    throw new Error(msg);
  }
  return (await resp.json()) as ScanResponse;
}

const SCAN_STEPS: { key: string; label: string }[] = [
  { key: "fetch",        label: "Fetching your website"          },
  { key: "voice",        label: "Analyzing brand voice"          },
  { key: "competitors",  label: "Reading positioning signals"    },
  { key: "seo",          label: "Reviewing marketing foundation" },
  { key: "campaigns",    label: "Generating campaign ideas"      },
  { key: "social",       label: "Assessing trust signals"        },
  { key: "consistency",  label: "Composing your executive report"},
];

function ScannerAurora(): JSX.Element {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute -left-40 -top-40 h-[600px] w-[600px] rounded-full opacity-50 blur-3xl"
           style={{ background: "radial-gradient(circle, rgba(229,180,84,0.12), transparent 60%)" }} />
      <div className="absolute -right-40 top-1/3 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl"
           style={{ background: "radial-gradient(circle, rgba(74,222,128,0.08), transparent 60%)" }} />
    </div>
  );
}

function ScannerTopbar(): JSX.Element {
  return (
    <header className="sticky top-0 z-30 border-b border-[color:var(--md-line)] backdrop-blur-xl"
            style={{ background: "linear-gradient(180deg, rgba(15,18,24,0.9), rgba(15,18,24,0.55))" }}>
      <div className="mx-auto flex max-w-[1080px] items-center gap-6 px-6 py-4">
        <Link to="/welcome" className="flex items-center gap-3">
          <span aria-hidden className="grid h-9 w-9 place-items-center rounded-[10px] font-display text-[16px] italic text-[var(--md-ink-0)]"
                style={{ background: "linear-gradient(135deg, var(--md-amber), #F5C15C)" }}>m</span>
          <span className="hidden flex-col leading-none sm:flex">
            <b className="orkexra-wordmark text-[13px]">ORKE<span className="x">X</span>RA</b>
            <span className="mt-1 md-eyebrow">Website Scanner · preview</span>
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/welcome" className="md-btn md-btn--ghost hidden sm:inline-flex" data-testid="scanner-exit">Back to site</Link>
          <Link to="/onboarding" className="md-btn md-btn--primary whitespace-nowrap" data-testid="scanner-cta-onboarding">
            <span className="hidden sm:inline">Build my Brand Brain</span>
            <span className="sm:hidden">Build mine</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

/* Normalizes user input into a display-friendly host, always tolerating garbage. */
function coerceUrl(input: string): { host: string; url: string } | null {
  const raw = input.trim();
  if (!raw) return null;
  const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const u = new URL(withProto);
    if (!u.hostname.includes(".")) return null;
    return { host: u.hostname.replace(/^www\./, ""), url: u.toString() };
  } catch { return null; }
}

type Phase = "idle" | "scanning" | "report";

export default function ScannerPage(): JSX.Element {
  const [input, setInput] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [target, setTarget] = useState<{ host: string; url: string } | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResponse | null>(null);

  const startScan = () => {
    const coerced = coerceUrl(input);
    if (!coerced) {
      setError("Enter a real website URL (e.g. yourbrand.com).");
      return;
    }
    setError(null);
    setTarget(coerced);
    setStepIdx(0);
    setResult(null);
    setPhase("scanning");
    // Silently capture the lead — never blocks the user.
    captureWaitlist({ source: "scanner", website: coerced.url });

    // Kick off the real backend scan in parallel with a paced UI advance.
    // The UI steps advance every ~600ms so the assessment feels considered
    // even when the backend returns in <200ms. When the backend resolves,
    // we snap to "report" (never before all steps have shown).
    const startedAt = Date.now();
    const stepTimer = window.setInterval(() => {
      setStepIdx((i) => (i < SCAN_STEPS.length - 1 ? i + 1 : i));
    }, 600);
    runExecutiveScan(coerced.url).then((r) => {
      const elapsed = Date.now() - startedAt;
      const minShowMs = 2400; // never faster than 2.4s — feels like real work
      const wait = Math.max(0, minShowMs - elapsed);
      window.setTimeout(() => {
        window.clearInterval(stepTimer);
        setStepIdx(SCAN_STEPS.length);
        setResult(r);
        setPhase("report");
      }, wait);
    }).catch((e: Error) => {
      window.clearInterval(stepTimer);
      setPhase("idle");
      setError(e.message || "We couldn't complete the assessment. Please try again.");
    });
  };

  const reset = () => {
    setPhase("idle");
    setStepIdx(0);
    setTarget(null);
    setResult(null);
  };

  return (
    <div data-testid="scanner-page" className="relative min-h-screen text-[var(--md-ivory-0)]">
      <ScannerAurora />
      <ScannerTopbar />

      <main className="mx-auto max-w-4xl px-6 pb-24 pt-14 md:pt-20">
        <AnimatePresence mode="wait">
          {phase === "idle"     && <IdlePanel input={input} setInput={setInput} onStart={startScan} error={error} />}
          {phase === "scanning" && target && <ScanningPanel target={target} stepIdx={stepIdx} />}
          {phase === "report"   && target && result && <ReportPanel target={target} result={result} onReset={reset} />}
        </AnimatePresence>
      </main>
    </div>
  );
}

/* ── Phase: idle ─────────────────────────────────────────────────── */
function IdlePanel({
  input, setInput, onStart, error,
}: {
  input: string;
  setInput: (v: string) => void;
  onStart: () => void;
  error: string | null;
}): JSX.Element {
  return (
    <motion.div
      key="idle"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.4 }}
      className="text-center"
    >
      <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/10 px-3 py-1 md-eyebrow md-eyebrow--amber">
        <Radar className="h-3 w-3" /> Website Scanner · preview
      </div>
      <h1 className="mt-8 font-display italic tracking-tight text-[var(--md-ivory-0)] text-[52px] leading-[0.98] md:text-[76px]">
        See what Orkexra<br />
        <span style={{ color: "var(--md-amber)" }}>can find on your site</span>.
      </h1>
      <p className="mx-auto mt-6 max-w-xl text-[16px] leading-relaxed text-[var(--md-fog)]">
        Paste your website for a <b className="text-[var(--md-ivory-0)]">preview scan</b> — the categories
        Orkexra analyses when it builds your Brand Brain: voice, competitors, SEO opportunities,
        and the first campaigns it would run.
      </p>
      <p className="mx-auto mt-2 max-w-xl text-[13px] text-[var(--md-fog)]">
        The full Business Brain™ scan runs live inside your workspace. This page is a preview of what
        it looks like.
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); onStart(); }}
        className="mx-auto mt-10 flex max-w-xl flex-wrap items-center gap-3"
      >
        <div className="relative flex-1 min-w-[240px]">
          <Globe2 className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--md-fog)]" />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="yourbrand.com"
            data-testid="scanner-url-input"
            className="w-full rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] py-3 pl-11 pr-4 text-[15px] text-[var(--md-ivory-0)] placeholder:text-[var(--md-fog)] focus:border-[var(--md-amber)] focus:outline-none"
          />
        </div>
        <button
          type="submit"
          data-testid="scanner-start"
          className="md-btn md-btn--primary"
          style={{ padding: "14px 22px", fontSize: 14 }}
        >
          Scan my site <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </form>

      {error && (
        <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-md border border-red-400/40 bg-red-500/10 px-3 py-1.5 text-[12px] text-red-200" data-testid="scanner-error">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      <div className="mt-10 grid gap-3 text-left md:grid-cols-3">
        {[
          { icon: <Target className="h-4 w-4" />,      title: "Brand voice",        line: "How your site actually reads today." },
          { icon: <TrendingUp className="h-4 w-4" />,  title: "Competitor set",     line: "The 3–5 brands you're really up against." },
          { icon: <Sparkles className="h-4 w-4" />,    title: "Campaign ideas",     line: "The first three campaigns we'd run for you." },
        ].map((c) => (
          <div key={c.title} className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-4">
            <div className="flex items-center gap-2 text-[var(--md-amber)]">{c.icon}<b className="text-[13px] text-[var(--md-ivory-0)]">{c.title}</b></div>
            <p className="mt-2 text-[12px] leading-relaxed text-[var(--md-fog)]">{c.line}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ── Phase: scanning ─────────────────────────────────────────────── */
function ScanningPanel({ target, stepIdx }: { target: { host: string; url: string }; stepIdx: number }): JSX.Element {
  return (
    <motion.div
      key="scanning"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="text-center"
    >
      <div className="mx-auto mb-8 grid h-20 w-20 place-items-center rounded-full border border-[color:var(--md-amber)]/40"
           style={{ background: "radial-gradient(circle, rgba(229,180,84,0.25), transparent 70%)" }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, ease: "linear", repeat: Infinity }}>
          <Search className="h-9 w-9 text-[var(--md-amber)]" />
        </motion.div>
      </div>
      <h2 className="font-display italic tracking-tight text-3xl text-[var(--md-ivory-0)] md:text-4xl">
        Running a preview scan of <span style={{ color: "var(--md-amber)" }}>{target.host}</span>…
      </h2>
      <p className="mt-3 md-eyebrow">This walks through the categories Orkexra analyses.</p>

      <ul className="mx-auto mt-10 max-w-md space-y-3 text-left" data-testid="scanner-steps">
        {SCAN_STEPS.map((s, i) => {
          const done = i < stepIdx;
          const active = i === stepIdx;
          return (
            <li key={s.key} className="flex items-center gap-3 rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-4 py-3">
              <span className="grid h-6 w-6 place-items-center rounded-full"
                    style={{
                      background: done ? "rgba(74,222,128,0.15)" : active ? "rgba(229,180,84,0.15)" : "var(--md-ink-3)",
                      border: `1px solid ${done ? "var(--md-good)" : active ? "var(--md-amber)" : "var(--md-line)"}`,
                    }}>
                {done ? <Check className="h-3 w-3 text-[var(--md-good)]" />
                  : active ? <Loader2 className="h-3 w-3 animate-spin text-[var(--md-amber)]" />
                  : <span className="h-1.5 w-1.5 rounded-full bg-[var(--md-fog)]" />}
              </span>
              <span className="text-[13px]" style={{ color: done || active ? "var(--md-ivory-0)" : "var(--md-fog)" }}>
                {s.label}
              </span>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}

/* ── Phase: report — REAL data from the scanner API ─────────────────── */
function ScoreDial({ label, score }: { label: string; score: number }): JSX.Element {
  const color = score >= 80 ? "var(--md-good)" : score >= 60 ? "var(--md-amber)" : "#F87171";
  return (
    <div className="flex flex-col items-center gap-1 rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] p-3">
      <div className="font-display text-2xl italic" style={{ color }}>{score}</div>
      <div className="text-[11px] uppercase tracking-wider text-[var(--md-fog)]">{label}</div>
    </div>
  );
}

function ReportPanel({
  target, result, onReset,
}: {
  target: { host: string; url: string };
  result: ScanResponse;
  onReset: () => void;
}): JSX.Element {
  const finding_icons: Record<string, JSX.Element> = {
    "Brand Voice":            <Target className="h-4 w-4" />,
    "Website Messaging":      <Sparkles className="h-4 w-4" />,
    "Trust & Authority":      <Check className="h-4 w-4" />,
    "Marketing Foundation":   <Radar className="h-4 w-4" />,
    "Marketing Opportunity":  <TrendingUp className="h-4 w-4" />,
    "Visual Hierarchy":       <Zap className="h-4 w-4" />,
  };
  return (
    <motion.div
      key="report"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full border border-[color:var(--md-good)]/40"
             style={{ background: "radial-gradient(circle, rgba(74,222,128,0.20), transparent 70%)" }}>
          <Check className="h-7 w-7 text-[var(--md-good)]" />
        </div>
        <div className="md-eyebrow md-eyebrow--amber">Executive Marketing Assessment</div>
        <h2 className="mt-3 font-display italic tracking-tight text-3xl text-[var(--md-ivory-0)] md:text-5xl">
          <span style={{ color: "var(--md-amber)" }}>{target.host}</span>
        </h2>
        <p className="mx-auto mt-3 md-eyebrow">
          {result.industry_label} · Classified with {Math.round(result.confidence * 100)}% confidence · {(result.elapsed_ms / 1000).toFixed(1)}s
        </p>
      </div>

      {/* Overall Marketing Health */}
      <div
        data-testid="scanner-report"
        className="mt-8 rounded-2xl border border-[color:var(--md-amber)]/30 p-6 md:p-8"
        style={{ background: "linear-gradient(180deg, rgba(20,24,33,0.94), rgba(15,18,24,0.85))" }}
      >
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex flex-col items-center rounded-xl border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/5 px-6 py-4">
            <div className="md-eyebrow md-eyebrow--amber">Marketing Health</div>
            <div className="font-display italic text-6xl" style={{ color: "var(--md-amber)" }} data-testid="scanner-overall-score">
              {result.overall_score}
            </div>
            <div className="text-xs text-[var(--md-fog)]">/ 100</div>
          </div>
          <p className="flex-1 min-w-[240px] text-[14px] leading-relaxed text-[var(--md-ivory-0)]" data-testid="scanner-executive-summary">
            {result.executive_summary}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3 md:grid-cols-6">
          <ScoreDial label="Voice"       score={result.scores.voice}       />
          <ScoreDial label="Messaging"   score={result.scores.messaging}   />
          <ScoreDial label="Trust"       score={result.scores.trust}       />
          <ScoreDial label="Foundation"  score={result.scores.foundation}  />
          <ScoreDial label="Opportunity" score={result.scores.opportunity} />
          <ScoreDial label="Hierarchy"   score={result.scores.visual_hierarchy} />
        </div>
      </div>

      {/* Findings */}
      <div className="mt-8 space-y-4">
        {result.findings.map((f) => (
          <div key={f.category} className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5"
               data-testid={`scanner-finding-${f.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-lg border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/10 text-[var(--md-amber)]">
                {finding_icons[f.category] ?? <Sparkles className="h-4 w-4" />}
              </span>
              <b className="text-[15px] text-[var(--md-ivory-0)]">{f.category}</b>
              <span className="ml-auto rounded-full border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/10 px-2 py-0.5 text-[12px] font-semibold text-[var(--md-amber)]">
                {f.score} / 100
              </span>
            </div>
            <p className="mt-3 text-[13px] leading-relaxed text-[var(--md-fog)]">{f.commentary}</p>
            {f.strengths.length > 0 && (
              <div className="mt-3">
                <div className="md-eyebrow" style={{ color: "var(--md-good)" }}>Strengths</div>
                <ul className="mt-1 space-y-1 text-[13px] text-[var(--md-ivory-0)]">
                  {f.strengths.map((s, i) => <li key={i} className="flex items-start gap-2"><Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-[var(--md-good)]" /> {s}</li>)}
                </ul>
              </div>
            )}
            {f.opportunities.length > 0 && (
              <div className="mt-3">
                <div className="md-eyebrow md-eyebrow--amber">Opportunities</div>
                <ul className="mt-1 space-y-1 text-[13px] text-[var(--md-ivory-0)]">
                  {f.opportunities.map((s, i) => <li key={i} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--md-amber)]" /> {s}</li>)}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* First three campaigns */}
      <div className="mt-10">
        <div className="md-eyebrow md-eyebrow--amber">First Three Campaigns Orkexra Would Launch</div>
        <h3 className="mt-2 font-display italic text-2xl md:text-3xl">Where we&rsquo;d start on Monday.</h3>
        <div className="mt-5 grid gap-4 md:grid-cols-3" data-testid="scanner-campaigns">
          {result.campaigns.map((c) => (
            <div key={c.title} className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5">
              <b className="block text-[14px] text-[var(--md-ivory-0)]">{c.title}</b>
              <div className="mt-1 md-eyebrow md-eyebrow--amber">{c.primary_channel}</div>
              <p className="mt-3 text-[12px] leading-relaxed text-[var(--md-fog)]"><b className="text-[var(--md-ivory-0)]">Objective —</b> {c.objective}</p>
              <p className="mt-2 text-[12px] leading-relaxed text-[var(--md-fog)]"><b className="text-[var(--md-ivory-0)]">Outcome —</b> {c.expected_outcome}</p>
              <p className="mt-2 text-[12px] leading-relaxed text-[var(--md-fog)]"><b className="text-[var(--md-ivory-0)]">Why —</b> {c.why_it_fits}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Content ideas + quick wins */}
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5">
          <div className="md-eyebrow md-eyebrow--amber">Content ideas</div>
          <ul className="mt-3 space-y-2 text-[13px] text-[var(--md-ivory-0)]">
            {result.content_ideas.map((c) => <li key={c} className="flex items-start gap-2"><Sparkles className="mt-0.5 h-3 w-3 flex-shrink-0 text-[var(--md-amber)]" /> {c}</li>)}
          </ul>
        </div>
        <div className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5">
          <div className="md-eyebrow" style={{ color: "var(--md-good)" }}>Quick wins</div>
          <ul className="mt-3 space-y-2 text-[13px] text-[var(--md-ivory-0)]">
            {result.quick_wins.map((c) => <li key={c} className="flex items-start gap-2"><Zap className="mt-0.5 h-3 w-3 flex-shrink-0 text-[var(--md-good)]" /> {c}</li>)}
          </ul>
        </div>
      </div>

      {/* Brand Brain preview */}
      <div className="mt-8 rounded-2xl border border-[color:var(--md-amber)]/30 bg-[var(--md-ink-1)] p-6 md:p-8">
        <div className="md-eyebrow md-eyebrow--amber">Brand Brain preview · Orkexra has identified</div>
        <h3 className="mt-2 font-display italic text-2xl md:text-3xl">The strategic core we&rsquo;d build for {target.host}.</h3>
        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <div><div className="md-eyebrow md-eyebrow--amber">Voice</div><p className="mt-1 text-[13px] text-[var(--md-ivory-0)]">{result.brand_brain_preview.voice}</p></div>
          <div><div className="md-eyebrow md-eyebrow--amber">Audience</div><p className="mt-1 text-[13px] text-[var(--md-ivory-0)]">{result.brand_brain_preview.audience}</p></div>
          <div>
            <div className="md-eyebrow md-eyebrow--amber">Products / services</div>
            <ul className="mt-1 space-y-0.5 text-[13px] text-[var(--md-ivory-0)]">
              {result.brand_brain_preview.products_services.map((s) => <li key={s}>· {s}</li>)}
            </ul>
          </div>
          <div>
            <div className="md-eyebrow md-eyebrow--amber">Core messaging</div>
            <ul className="mt-1 space-y-0.5 text-[13px] text-[var(--md-ivory-0)]">
              {result.brand_brain_preview.core_messaging.map((s) => <li key={s}>· {s}</li>)}
            </ul>
          </div>
        </div>
      </div>

      {/* Unlock CTA */}
      <div className="mt-12 rounded-2xl border border-[color:var(--md-amber)]/35 p-6 md:p-8"
           style={{ background: "linear-gradient(180deg, rgba(20,24,33,0.94), rgba(15,18,24,0.85))", boxShadow: "0 40px 100px -50px rgba(0,0,0,0.55)" }}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-lg">
            <div className="md-eyebrow md-eyebrow--amber">Ready to run this for real?</div>
            <h3 className="mt-2 font-display italic text-2xl md:text-3xl">
              Your <span style={{ color: "var(--md-amber)" }}>Brand Brain</span> is ready to build.
            </h3>
            <p className="mt-2 text-[14px] leading-relaxed text-[var(--md-fog)]">
              Create your workspace to save this assessment, generate your
              first campaign, and turn every finding into shippable work.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/onboarding" data-testid="scanner-cta-build" className="md-btn md-btn--primary" style={{ padding: "14px 24px", fontSize: 14 }}>
              Build my Brand Brain <ChevronRight className="h-3.5 w-3.5" />
            </Link>
            <button type="button" onClick={onReset} data-testid="scanner-cta-scan-another" className="md-btn md-btn--ghost" style={{ padding: "14px 22px", fontSize: 14 }}>
              Scan another site
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Legacy ReportCard removed — real report renders findings inline above. */

